/**
 * A client's interface partners as the IRES Admin Client shows them (Track #2610084, merge 2): what
 * `listClientPartnersForAdministration` and `getClientPartnerForAdministration` serve. IRES Cloud is the
 * authority (`services/externalSystems/PartnerAdministrationFields.ts`); see `docs/admin/external-systems.md`.
 * A secret (API key, password) is never served: only whether it is set.
 */

/** Where the partner stands with IRES; PAUSED: IRES sends it nothing, the partner can still call IRES. */
export type PartnerStatus = "SETUP" | "TEST" | "LIVE" | "PAUSED";

export const PARTNER_STATUSES: PartnerStatus[] = ["SETUP", "TEST", "LIVE", "PAUSED"];

/** How IRES signs in to the partner. */
export type PartnerSignIn = "API_KEY" | "SIGN_IN" | "EASY_TEC";

export type PartnerEndpointField = "createProject" | "projectData" | "projectStatus" | "noteUpdate";

export const PARTNER_ENDPOINT_FIELDS: PartnerEndpointField[] = ["createProject", "projectData", "projectStatus", "noteUpdate"];

/** "Was ... bekommt": what IRES sends the partner. */
export type PartnerAcceptsField =
  | "acceptsNewProjects"
  | "acceptsProjectDataUpdates"
  | "acceptsProjectStatusUpdates"
  | "acceptsNotes"
  | "includeProjectOwnersInfo"
  | "sendsExtraPhaseData";

export const PARTNER_ACCEPTS_FIELDS: PartnerAcceptsField[] = [
  "acceptsNewProjects",
  "acceptsProjectDataUpdates",
  "acceptsProjectStatusUpdates",
  "acceptsNotes",
  "includeProjectOwnersInfo",
  "sendsExtraPhaseData",
];

/** One partner on the Externe Systeme card. */
export interface ClientPartnerSummary {
  id: string;
  name: string;
  /** EASY_TEC, EXTERNAL, or null for a partner that only calls IRES. */
  system: string | null;
  status: PartnerStatus;
  /** The client it belongs to; for a group, the MASTER-Mandant. */
  ownerClientId: string;
  /** False on a member, which shows its master's partner read-only. */
  editable: boolean;
}

/** One partner's page. */
export interface ClientPartner extends ClientPartnerSummary {
  connection: {
    baseUrl: string | null;
    endpoints: Record<PartnerEndpointField, string | null>;
    signIn: PartnerSignIn | null;
    identifiers: { email: string | null; clientId: string | null; authUrl: string | null; tokenUrl: string | null };
    secretsSet: { apiKey: boolean; password: boolean };
  };
  accepts: Record<PartnerAcceptsField, boolean>;
  behaviour: {
    appointmentFieldSwap: boolean;
    /** Null: no setting, the EasyTec route decides. */
    incomingContactPerson0: boolean | null;
    /** `latestProjectStatus` on every step IRES sends and on GET projectStatus (MDIS, 2026-10-15). */
    sendsLatestProjectStatus: boolean;
    isTestSystem: boolean;
  };
  /** The test-system switch may be turned on: the owner client has the ERP-Simulator on. */
  mayBeTestSystem: boolean;
  /** The system's own access to IRES (merge 3): the address it signs in with; its password is never served. Absent from an IRES Cloud before merge 3. */
  access?: { email: string | null };
  /** "Verknüpfte Mandanten" (merge 3): only on the owner's page, absent from an older IRES Cloud. */
  links?: ClientPartnerLinks;
  /** The partner's own contact people (merge 4); absent from an older IRES Cloud. */
  contacts?: PartnerContact[];
  /** The go-live checklist (merge 4), worked out when the page opens; only on the owner's page. */
  goLive?: GoLiveCheck[];
}

/** The role of a partner's contact person: Technik or Fachlich. */
export type PartnerContactRole = "TECHNICAL" | "BUSINESS";

export const PARTNER_CONTACT_ROLES: PartnerContactRole[] = ["TECHNICAL", "BUSINESS"];

/** The most contact people IRES Cloud keeps for a system. */
export const MAX_PARTNER_CONTACTS = 20;

/** One of the partner's own people (merge 4, Taimoor 2026-10-27). */
export interface PartnerContact {
  name: string;
  role: PartnerContactRole;
  email: string | null;
  phone: string | null;
}

/**
 * One item of the go-live checklist (merge 4): automatic, never ticked by hand, and information only
 * (Live is never blocked). CONNECTION is listed only for a system IRES sends to.
 */
export type GoLiveCheckKey = "CONTACT" | "SIGNED_IN" | "CONNECTION" | "PROJECT";

export interface GoLiveCheck {
  key: GoLiveCheckKey;
  done: boolean;
  /** SIGNED_IN: when the system last signed in or renewed its sign-in (epoch ms). */
  atMillis: number | null;
}

/** One client in a system's links. */
export interface ClientPartnerLinkClient {
  id: string;
  name: string;
}

/** The clients linked to a system (the owner first), and the owner's group members that could be. */
export interface ClientPartnerLinks {
  clients: Array<ClientPartnerLinkClient & { owner: boolean }>;
  candidates: ClientPartnerLinkClient[];
}

/** One row of the portal-wide "Externe Systeme" list (`listExternalSystemsForAdministration`). */
export interface ExternalSystemRow {
  id: string;
  name: string;
  system: string | null;
  status: PartnerStatus;
  ownerClientId: string;
  ownerName: string;
  linkedClientNames: string[];
}

/** The endpoint groups a package is ticked by (#2610090); SIGN_IN is always part of it. */
export type PartnerEndpointGroup = "SIGN_IN" | "PROJECTS" | "STATUS" | "NOTES" | "REPORTS" | "DEVICES" | "DEVICE_CATEGORIES" | "USERS";

/** The three files of a package: the cut-down description, the same as a page, and the overview PDF. */
export type PartnerPackageFile = "yaml" | "html" | "overview";

export const PARTNER_PACKAGE_FILES: PartnerPackageFile[] = ["yaml", "html", "overview"];

/** Whom a package was handed to. */
export interface PartnerPackageRecipient {
  name: string;
  email: string | null;
}

/**
 * Whether a package still matches what IRES would hand over today (#2610090 merge 2). Only the newest
 * package of a system is checked; older ones are SUPERSEDED. UNCHECKED: its stored description could not
 * be read in IRES Cloud.
 */
export type PartnerPackageState = "CURRENT" | "OUTDATED" | "SUPERSEDED" | "UNCHECKED";

/** One endpoint of the API description, as the page names it ("GET /projectStatus"). */
export interface PartnerPackageOperation {
  method: string;
  path: string;
}

/** The settings an overview states, as IRES Cloud stores them with a package (`OVERVIEW_SETTING_KEYS`). */
export type PartnerPackageSettingKey =
  | "name"
  | "accessEmail"
  | "ownerIsGroup"
  | "sends"
  | "baseUrl"
  | "signIn"
  | "newProjects"
  | "projectData"
  | "projectStatus"
  | "notes"
  | "includeProjectOwnersInfo"
  | "sendsExtraPhaseData"
  | "appointmentFieldSwap"
  | "incomingContactPerson0"
  | "sendsLatestProjectStatus"
  | "isTestSystem";

/** One setting that changed since the package; for what IRES sends, the address or null when not sent. */
export interface PartnerPackageSettingChange {
  key: PartnerPackageSettingKey;
  before: string | boolean | null;
  after: string | boolean | null;
}

/** One API version after the package's, with what changed in it (English, as the partner's documents). */
export interface PartnerApiVersion {
  version: string;
  date: string;
  changes: string[];
}

/** Why the newest package is out of date. */
export interface PartnerPackageOutdated {
  operations: { added: PartnerPackageOperation[]; removed: PartnerPackageOperation[]; changed: PartnerPackageOperation[] };
  /** The base URL (`SERVERS`) or the sign-in schemes (`SECURITY`) of the description. */
  general: Array<"SERVERS" | "SECURITY">;
  settings: PartnerPackageSettingChange[];
  /** False for a package made before its settings were stored: only the API was checked. */
  settingsChecked: boolean;
  changelog: PartnerApiVersion[];
}

/** One integration package of a system (`listClientPartnerPackagesForAdministration`). */
export interface ClientPartnerPackage {
  id: string;
  createdAtMillis: number | null;
  createdByName: string | null;
  apiVersion: string;
  groups: PartnerEndpointGroup[];
  recipient: PartnerPackageRecipient;
  state: PartnerPackageState;
  /** Only on the newest package, when it was checked. */
  outdated?: PartnerPackageOutdated;
}

/** A system's packages and what the create dialog offers; `available` false for an EasyTec system. */
export interface ClientPartnerPackageList {
  available: boolean;
  packages: ClientPartnerPackage[];
  groups: PartnerEndpointGroup[];
  startGroups: PartnerEndpointGroup[];
  apiVersion: string;
}

/** The Externe Systeme card: the systems, and whether this client may create one (not a group member). */
export interface ClientPartnerList {
  partners: ClientPartnerSummary[];
  mayCreate: boolean;
}

/** How a new external system gets steps; NONE: it only calls IRES. */
export type PartnerSystemChoice = "EXTERNAL" | "EASY_TEC" | "NONE";

export const PARTNER_SYSTEM_CHOICES: PartnerSystemChoice[] = ["EXTERNAL", "EASY_TEC", "NONE"];

/** A new external system as `createClientPartnerForAdministration` takes it (merge 3). */
export interface PartnerCreateRequest {
  name: string;
  email: string;
  system: PartnerSystemChoice;
  /** API_KEY or SIGN_IN for EXTERNAL; null otherwise. */
  signIn: "API_KEY" | "SIGN_IN" | null;
  baseUrl: string | null;
}

/** The card groups; each change is one Verlauf entry. */
export type PartnerChangeGroup = "PARTNER_STATUS" | "PARTNER_CONNECTION" | "PARTNER_ACCEPTS" | "PARTNER_BEHAVIOUR" | "PARTNER_CONTACTS";

/** One card's change as `updateClientPartnerForAdministration` takes it. */
export type PartnerChanges = Record<string, { expected: unknown; value: unknown }>;
